import {
  getReferralCodes,
  normalizeReferralCode,
  REFERRAL_PREFIX,
} from "@/cart/referralCode";
import {
  getReferralAttribute,
  mergeReferralAttribute,
} from "@/cart/referralAttribute";
import type { CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";

let inFlightReferralCode: string | undefined;
let inFlightReferralPromise: Promise<Cart | void> | null = null;

const getUniqueCodes = (codes: string[]) => {
  return Array.from(new Set(codes.map((code) => code.toUpperCase())));
};

export const ensureReferralCode = ({ get, set }: CartStateGetSet) => {
  return async (code: string) => {
    const normalizedCode = normalizeReferralCode(code);

    if (!normalizedCode) {
      return;
    }

    set({ referralCode: normalizedCode });

    const currentCart = get().cart;
    if (getReferralCodes(currentCart).includes(normalizedCode)) {
      return currentCart;
    }

    if (
      inFlightReferralCode === normalizedCode &&
      inFlightReferralPromise !== null
    ) {
      return inFlightReferralPromise;
    }

    inFlightReferralCode = normalizedCode;
    const applyReferralCode = async () => {
      let { cartId, cart: existingCart } = get();

      if (getReferralCodes(existingCart).includes(normalizedCode)) {
        return existingCart;
      }

      if (!cartId) {
        const { cartCreate } = await GRAPHQL_CLIENT.CartCreate({
          input: { lines: [] },
        });

        const createdCart = cartCreate?.cart as Cart | undefined;

        if (!createdCart) {
          throw new Error(
            "ensureReferralCode: Creating cart for referral code failed",
          );
        }

        cartId = createdCart.id;
        existingCart = createdCart;
        set({
          cartId: createdCart.id,
          cart: createdCart,
          referralCode: normalizedCode,
        });
      }

      const existingCodes =
        existingCart?.discountCodes?.map((discountCode) => discountCode.code) ||
        [];
      const { cartDiscountCodesUpdate } = await GRAPHQL_CLIENT.AddDiscountCodes(
        {
          cartId,
          discountCodes: getUniqueCodes([...existingCodes, normalizedCode]),
        },
      );
      const updatedCart = cartDiscountCodesUpdate?.cart as Cart | undefined;

      if (!updatedCart) {
        throw new Error(
          "ensureReferralCode: Adding referral code to cart failed",
        );
      }

      set({
        cartId: updatedCart.id,
        cart: updatedCart,
        referralCode: normalizedCode,
      });

      // FRIEND- codes skip the marker unless a stale bare marker needs refreshing.
      if (
        normalizedCode.startsWith(REFERRAL_PREFIX) &&
        !getReferralAttribute(updatedCart)
      ) {
        return updatedCart;
      }

      try {
        await get().setCartAndLineAttributes(undefined, {
          cartAttributes: mergeReferralAttribute(updatedCart, normalizedCode),
          lineAttributes: {},
        });
        return get().cart ?? updatedCart;
      } catch (error) {
        console.error("Failed to record referral marker on cart:", error);
        return updatedCart;
      }
    };

    inFlightReferralPromise = applyReferralCode()
      .catch((error) => {
        const latestCart = get().cart;

        if (
          get().referralCode === normalizedCode &&
          !getReferralCodes(latestCart).includes(normalizedCode)
        ) {
          set({ referralCode: undefined });
        }

        throw error;
      })
      .finally(() => {
        if (inFlightReferralCode === normalizedCode) {
          inFlightReferralCode = undefined;
          inFlightReferralPromise = null;
        }
      });

    return inFlightReferralPromise;
  };
};
